import React, { useState } from "react";
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Flame,
  Globe,
  LayoutDashboard,
  Layers,
  ShieldCheck,
  SlidersHorizontal,
  TrendingUp,
  HelpCircle,
} from "lucide-react";
import type { AdsTrackingClientPayload } from "../../types";

interface Props {
  data: AdsTrackingClientPayload;
}

type MobileTab = "resumen" | "pacing" | "tracking" | "alertas";

export function AdsTrackingMobile({ data }: Props) {
  const [activeTab, setActiveTab] = useState<MobileTab>("resumen");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [expandedLanding, setExpandedLanding] = useState<string | null>(null);

  const { summary, campaigns, landing_pages, alerts, daily_history } = data;

  return (
    <div className="at-mobile-container">
      {/* 1. SECCIÓN RESUMEN MOBILE */}
      {activeTab === "resumen" && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 4px", color: "var(--at-ink)" }}>
              {data.client_name}
            </h2>
            <p style={{ fontSize: "12px", color: "var(--at-muted)", margin: 0 }}>
              Auditoría BigQuery: Pacing MTD y Tracking GTM
            </p>
          </div>

          {/* Carrusel Horizontal de KPIs (Rail con Snap) */}
          <div className="at-m-kpi-rail" role="region" aria-label="Indicadores Clave">
            <div className="at-m-kpi-card">
              <div className="at-kpi-label">
                <span>Score GTM</span>
                <ShieldCheck size={16} />
              </div>
              <div className="at-kpi-val">
                {summary.health_score !== null ? `${summary.health_score} / 100` : "Sin auditar"}
              </div>
              <div className="at-kpi-sub">
                {summary.health_score !== null ? "vw_dm_gtm_health" : "Sin registro en BQ"}
              </div>
            </div>

            <div className="at-m-kpi-card">
              <div className="at-kpi-label">
                <span>Pacing MTD</span>
                <TrendingUp size={16} />
              </div>
              <div className="at-kpi-val">
                {summary.pacing_mtd_pct !== null ? `${summary.pacing_mtd_pct}%` : "N/D"}
              </div>
              <div className="at-kpi-sub">
                {summary.spend_mtd !== null ? `$${Math.round(summary.spend_mtd).toLocaleString("es-CL")} gastados` : "Sin datos MTD"}
              </div>
            </div>

            <div className="at-m-kpi-card">
              <div className="at-kpi-label">
                <span>Intradía</span>
                <Flame size={16} />
              </div>
              <div className="at-kpi-val" style={{ fontSize: "16px" }}>
                Sin Conector
              </div>
              <div className="at-kpi-sub">
                Requiere Ads MCP
              </div>
            </div>

            <div className={`at-m-kpi-card ${summary.p0_alerts > 0 ? "is-alert" : ""}`}>
              <div className="at-kpi-label">
                <span>Hallazgos Reales</span>
                <AlertOctagon size={16} />
              </div>
              <div className="at-kpi-val">{summary.p0_alerts + summary.p1_alerts}</div>
              <div className="at-kpi-sub" style={{ color: summary.p0_alerts > 0 ? "var(--at-p0)" : "inherit" }}>
                {summary.p0_alerts} P0 en GTM
              </div>
            </div>
          </div>

          {/* Alertas Reales Destacadas */}
          {alerts.filter((a) => a.severity === "P0").length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--at-p0)", marginBottom: "8px", textTransform: "uppercase" }}>
                Hallazgos Críticos en BigQuery
              </div>
              {alerts
                .filter((a) => a.severity === "P0")
                .map((a) => (
                  <div
                    key={a.id}
                    className="at-m-card"
                    style={{ borderLeft: "4px solid var(--at-p0)", marginBottom: "8px" }}
                    onClick={() => setActiveTab("alertas")}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                      <span className="at-badge p0">P0 Crítica</span>
                      <span className="at-badge neutral">{a.category}</span>
                    </div>
                    <div className="at-m-card-title">{a.title}</div>
                    <div className="at-m-card-body" style={{ marginTop: "6px" }}>{a.description}</div>
                  </div>
                ))}
            </div>
          )}

          {/* Landings Auditadas */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h3 style={{ fontSize: "15px", fontWeight: 700, margin: 0, color: "var(--at-ink)" }}>
                Landings Escaneadas ({landing_pages.length})
              </h3>
              <button
                type="button"
                onClick={() => setActiveTab("tracking")}
                style={{ background: "none", border: "none", color: "var(--at-accent)", fontSize: "12px", fontWeight: 600 }}
              >
                Ver todas →
              </button>
            </div>

            {landing_pages.length > 0 ? (
              <div className="at-m-card-list">
                {landing_pages.slice(0, 3).map((l) => (
                  <div key={l.url} className="at-m-card" onClick={() => setActiveTab("tracking")}>
                    <div className="at-m-card-header">
                      <span className="at-badge neutral">{l.http_status !== null ? `HTTP ${l.http_status}` : "HTTP No medido"}</span>
                      {l.health_score !== null && (
                        <span style={{ fontSize: "12px", fontWeight: 700 }}>
                          Score: {l.health_score}/100
                        </span>
                      )}
                    </div>
                    <div className="at-m-card-title" style={{ wordBreak: "break-all" }}>
                      {l.url.replace("https://", "")}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="at-m-card" style={{ textAlign: "center", padding: "20px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "var(--at-muted)" }}>
                  Sin escaneo en <code>site_scan_snapshot</code>
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* 2. SECCIÓN PACING MTD */}
      {activeTab === "pacing" && (
        <div>
          <div style={{ marginBottom: "14px" }}>
            <h3 style={{ fontSize: "17px", fontWeight: 700, margin: "0 0 4px" }}>Pacing e Historial Real</h3>
            <small style={{ color: "var(--at-muted)" }}>Fuente: vw_dm_spend_budget_daily</small>
          </div>

          <div className="at-m-card" style={{ marginBottom: "16px" }}>
            <div className="at-m-card-title">Pacing Mes Acumulado (MTD)</div>
            <div style={{ fontSize: "20px", fontWeight: 700, margin: "8px 0" }}>
              {summary.pacing_mtd_pct !== null ? `${summary.pacing_mtd_pct}%` : "Sin meta mensual"}
            </div>
            {summary.pacing_mtd_pct !== null && (
              <div className="at-progress-track" style={{ height: "8px" }}>
                <div
                  className={`at-progress-bar ${summary.pacing_mtd_pct > 110 ? "is-over" : "is-good"}`}
                  style={{ width: `${Math.min(100, summary.pacing_mtd_pct)}%` }}
                />
              </div>
            )}
            <p style={{ fontSize: "12px", color: "var(--at-muted)", margin: "8px 0 0" }}>
              Gasto MTD: {summary.spend_mtd !== null ? `$${Math.round(summary.spend_mtd).toLocaleString("es-CL")}` : "N/D"}
            </p>
          </div>

          <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px" }}>
            Historial Diario (Últimas 2 Semanas)
          </div>
          {daily_history.length > 0 ? (
            <div className="at-m-card-list">
              {daily_history.map((d) => (
                <div key={d.date} className="at-m-card" style={{ padding: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 600 }}>
                    <span>{d.date}</span>
                    <span>${Math.round(d.spend_daily).toLocaleString("es-CL")}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--at-muted)", marginTop: "4px" }}>
                    <span>Presupuesto: ${Math.round(d.budget_daily).toLocaleString("es-CL")}</span>
                    <span>{d.pacing_pct !== null ? `${d.pacing_pct}%` : "Sin meta"}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="at-m-card" style={{ textAlign: "center", padding: "24px" }}>
              <p style={{ margin: 0, fontSize: "12px", color: "var(--at-muted)" }}>
                No hay filas de gasto diario en BigQuery para este cliente.
              </p>
            </div>
          )}
        </div>
      )}

      {/* 3. SECCIÓN TRACKING & LANDINGS REALES */}
      {activeTab === "tracking" && (
        <div>
          <div style={{ marginBottom: "14px" }}>
            <h3 style={{ fontSize: "17px", fontWeight: 700, margin: "0 0 4px" }}>Landings & Atribución</h3>
            <small style={{ color: "var(--at-muted)" }}>Fuente: site_scan_snapshot</small>
          </div>

          {landing_pages.length > 0 ? (
            <div className="at-m-card-list">
              {landing_pages.map((l) => {
                const isExpanded = expandedLanding === l.url;
                return (
                  <div
                    key={l.url}
                    className="at-m-card"
                    onClick={() => setExpandedLanding(isExpanded ? null : l.url)}
                  >
                    <div className="at-m-card-header">
                      <span className="at-badge neutral">{l.http_status !== null ? `HTTP ${l.http_status}` : "HTTP No medido"}</span>
                      {l.health_score !== null && (
                        <span style={{ fontSize: "13px", fontWeight: 700 }}>
                          Score {l.health_score}/100
                        </span>
                      )}
                    </div>

                    <div className="at-m-card-title" style={{ wordBreak: "break-all" }}>
                      {l.url.replace("https://", "")}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", margin: "10px 0" }}>
                      <span className={`at-badge ${l.trackers_in_container > 0 ? "success" : "neutral"}`}>
                        GTM: {l.trackers_in_container > 0 ? `${l.trackers_in_container} tags dentro` : "0 tags dentro"}
                      </span>
                      <span className={`at-badge ${l.ga4_id ? "success" : "neutral"}`}>
                        GA4: {l.ga4_id || "No"}
                      </span>
                      <span className={`at-badge ${l.meta_pixel_id ? "success" : "neutral"}`}>
                        Pixel: {l.meta_pixel_id || "No"}
                      </span>
                      <span className={`at-badge ${l.consent_banner_found ? "success" : "neutral"}`}>
                        Banner: {l.consent_banner_found ? "Sí" : "No"}
                      </span>
                      {l.fired_before_interaction && (
                        <span className="at-badge p1">Pre-click: Sí</span>
                      )}
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--at-border-soft)" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>Hallazgos Observados:</div>
                        {l.findings.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "var(--at-muted)" }}>
                            {l.findings.map((f, i) => (
                              <li key={i} style={{ marginBottom: "4px" }}>{f}</li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ margin: 0, fontSize: "12px", color: "var(--at-muted)" }}>Sin anomalías en el escaneo.</p>
                        )}
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="at-m-card-action"
                        >
                          Visitar Landing <ExternalLink size={14} />
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="at-m-card" style={{ textAlign: "center", padding: "32px 16px" }}>
              <HelpCircle size={32} style={{ color: "var(--at-muted)", margin: "0 auto 12px" }} />
              <h4 style={{ margin: "0 0 6px", fontSize: "15px" }}>Sin Escaneo Web en BigQuery</h4>
              <p style={{ color: "var(--at-muted)", fontSize: "12px", margin: 0 }}>
                Corre <code>scan_site_tracking.py</code> para escanear y persistir trackers de este cliente.
              </p>
            </div>
          )}
        </div>
      )}

      {/* 4. SECCIÓN ALERTAS REALES */}
      {activeTab === "alertas" && (
        <div>
          <div style={{ marginBottom: "14px" }}>
            <h3 style={{ fontSize: "17px", fontWeight: 700, margin: "0 0 4px" }}>Hallazgos GTM Reales</h3>
            <small style={{ color: "var(--at-muted)" }}>Fuente: vw_dm_gtm_findings</small>
          </div>

          {alerts.length > 0 ? (
            <div className="at-m-card-list">
              {alerts.map((al) => (
                <div
                  key={al.id}
                  className="at-m-card"
                  style={{ borderLeft: `4px solid ${al.severity === "P0" ? "var(--at-p0)" : "var(--at-p1)"}` }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <span className={`at-badge ${al.severity.toLowerCase()}`}>{al.severity}</span>
                    <span className="at-badge neutral">{al.category}</span>
                  </div>

                  <div className="at-m-card-title">{al.title}</div>
                  <p style={{ fontSize: "12px", color: "var(--at-muted)", margin: "8px 0 12px", lineHeight: 1.4 }}>
                    {al.description}
                  </p>

                  <div style={{ background: "var(--at-surface-container)", padding: "10px", borderRadius: "8px", fontSize: "12px" }}>
                    <div style={{ fontWeight: 700, color: "var(--at-accent)", marginBottom: "4px" }}>Acción</div>
                    <div>{al.action_recommended}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="at-m-card" style={{ textAlign: "center", padding: "32px 16px" }}>
              <CheckCircle2 size={32} style={{ color: "var(--at-success)", margin: "0 auto 12px" }} />
              <h4 style={{ margin: "0 0 6px", fontSize: "15px" }}>0 Hallazgos en BigQuery</h4>
              <p style={{ color: "var(--at-muted)", fontSize: "12px", margin: 0 }}>
                No se registran alertas en <code>vw_dm_gtm_findings</code>.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Barra de Navegación Inferior (Material 3 Bottom Navigation) */}
      <nav className="at-m-bottomnav" aria-label="Navegación Móvil">
        <button
          type="button"
          className={`at-m-navitem ${activeTab === "resumen" ? "is-active" : ""}`}
          onClick={() => setActiveTab("resumen")}
        >
          <div className="at-m-navicon-wrap"><LayoutDashboard size={20} /></div>
          <span>Resumen</span>
        </button>

        <button
          type="button"
          className={`at-m-navitem ${activeTab === "pacing" ? "is-active" : ""}`}
          onClick={() => setActiveTab("pacing")}
        >
          <div className="at-m-navicon-wrap"><TrendingUp size={20} /></div>
          <span>Pacing</span>
        </button>

        <button
          type="button"
          className={`at-m-navitem ${activeTab === "tracking" ? "is-active" : ""}`}
          onClick={() => setActiveTab("tracking")}
        >
          <div className="at-m-navicon-wrap"><Globe size={20} /></div>
          <span>Tracking</span>
        </button>

        <button
          type="button"
          className={`at-m-navitem ${activeTab === "alertas" ? "is-active" : ""}`}
          onClick={() => setActiveTab("alertas")}
        >
          <div className="at-m-navicon-wrap">
            <AlertTriangle size={20} />
            {summary.p0_alerts > 0 && <span className="at-m-navbadge">{summary.p0_alerts}</span>}
          </div>
          <span>Alertas</span>
        </button>
      </nav>
    </div>
  );
}
